import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  gatewayGovernanceApi,
  type APIPolicy,
  type GatewayAnalytics,
  type GatewayDashboard,
  type GatewayDeveloperEntry,
  type GatewayHealth,
  type GatewaySDKEntry,
  type OpenAPIRegistryEntry,
  type OpenAPIValidationResult,
  type RegisteredAPI,
} from '../../api/developer';

type Tab = 'overview' | 'registry' | 'openapi' | 'sdk' | 'developers' | 'policies' | 'analytics' | 'health' | 'lifecycle';
type Theme = 'light' | 'dark';

function lifecycleColor(lc: string, dark: boolean) {
  if (lc === 'published') return dark ? 'text-emerald-400' : 'text-emerald-600';
  if (lc === 'deprecated') return dark ? 'text-amber-400' : 'text-amber-600';
  if (lc === 'draft') return dark ? 'text-slate-400' : 'text-slate-500';
  return dark ? 'text-indigo-400' : 'text-indigo-600';
}

export function APIGatewayGovernancePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<GatewayDashboard | null>(null);
  const [apis, setApis] = useState<RegisteredAPI[]>([]);
  const [specs, setSpecs] = useState<OpenAPIRegistryEntry[]>([]);
  const [sdks, setSdks] = useState<GatewaySDKEntry[]>([]);
  const [developers, setDevelopers] = useState<GatewayDeveloperEntry[]>([]);
  const [policies, setPolicies] = useState<APIPolicy[]>([]);
  const [health, setHealth] = useState<GatewayHealth | null>(null);
  const [analytics, setAnalytics] = useState<GatewayAnalytics | null>(null);
  const [familyFilter, setFamilyFilter] = useState('');
  const [validateResult, setValidateResult] = useState<OpenAPIValidationResult | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, apiList, oas, sdkList, devs, pol, hlth, an] = await Promise.all([
        gatewayGovernanceApi.dashboard(),
        gatewayGovernanceApi.listAPIs(),
        gatewayGovernanceApi.listOpenAPI(),
        gatewayGovernanceApi.listSDKs(),
        gatewayGovernanceApi.listDevelopers(),
        gatewayGovernanceApi.policies(),
        gatewayGovernanceApi.health(),
        gatewayGovernanceApi.analytics(),
      ]);
      setDashboard(dash);
      setApis(apiList);
      setSpecs(oas);
      setSdks(sdkList);
      setDevelopers(devs);
      setPolicies(pol);
      setHealth(hlth);
      setAnalytics(an);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load API gateway');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateSpec = async (apiName: string) => {
    setBusy(true);
    setValidateResult(null);
    try {
      setValidateResult(await gatewayGovernanceApi.validateOpenAPI({ api_name: apiName }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const deprecateAPI = async (apiId: string) => {
    setBusy(true);
    setActionMsg(null);
    try {
      await gatewayGovernanceApi.deprecateAPI({ api_id: apiId, reason: 'Deprecated via lifecycle dashboard' });
      setActionMsg('API deprecated');
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Deprecation failed');
    } finally {
      setBusy(false);
    }
  };

  const families = [...new Set(apis.map((a) => a.product_family).filter(Boolean))] as string[];
  const filteredAPIs = familyFilter ? apis.filter((a) => a.product_family === familyFilter) : apis;
  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'registry', label: 'API Registry' },
    { id: 'openapi', label: 'OpenAPI Explorer' },
    { id: 'sdk', label: 'SDK Manager' },
    { id: 'developers', label: 'Developer Portal' },
    { id: 'policies', label: 'Policy Manager' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'health', label: 'Gateway Health' },
    { id: 'lifecycle', label: 'Lifecycle' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-teal-500">DEV-000080</p>
          <h1 className="text-2xl font-semibold">Enterprise API Gateway</h1>
          <p className={`text-sm ${muted}`}>Unified ingress, OpenAPI registry, SDK generation & developer portal</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/developer" className="text-teal-500 hover:underline">Developer Portal</Link>
            <span className={muted}>·</span>
            <Link to="/platform/integration-governance" className="text-teal-500 hover:underline">Integration Governance</Link>
            <span className={muted}>·</span>
            <Link to="/platform/event-mesh" className="text-teal-500 hover:underline">Event Mesh</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
          <button type="button" onClick={refresh} disabled={busy} className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50">Refresh</button>
        </div>
      </div>

      {error && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-red-900 bg-red-950 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{error}</div>}
      {actionMsg && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-emerald-900 bg-emerald-950 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>{actionMsg}</div>}
      {validateResult && <div className={`rounded-lg border px-4 py-3 text-sm ${validateResult.valid ? (dark ? 'border-emerald-900 bg-emerald-950 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800') : (dark ? 'border-amber-900 bg-amber-950 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-800')}`}>{validateResult.message}</div>}

      <div className="flex flex-wrap gap-2 border-b pb-2" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-teal-600 text-white' : dark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && health && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>API Health Score</p><p className="mt-1 text-3xl font-semibold text-teal-500">{health.api_health_score.toFixed(1)}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Registered APIs</p><p className="mt-1 text-3xl font-semibold">{health.registered_apis}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>OpenAPI Specs</p><p className="mt-1 text-3xl font-semibold">{health.openapi_specs}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>SDK Packages</p><p className="mt-1 text-3xl font-semibold">{health.sdk_packages}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Developer Experience</p><p className="mt-1 text-2xl font-semibold">{health.developer_experience_score.toFixed(1)}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Contract Compliance</p><p className="mt-1 text-2xl font-semibold">{health.contract_compliance.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Active Developers</p><p className="mt-1 text-2xl font-semibold">{health.active_developers}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Availability</p><p className="mt-1 text-2xl font-semibold">{health.service_availability.toFixed(1)}%</p></div>
        </div>
      )}

      {tab === 'registry' && (
        <div className="space-y-4">
          <select value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`} aria-label="Filter by product family">
            <option value="">All families</option>
            {families.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <div className="overflow-x-auto rounded-xl border">
            <table className={`min-w-full text-left text-sm ${card}`}>
              <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
                <tr><th className="px-4 py-2 font-medium">API</th><th className="px-4 py-2 font-medium">Service</th><th className="px-4 py-2 font-medium">Lifecycle</th><th className="px-4 py-2 font-medium">Auth</th><th className="px-4 py-2 font-medium">Rate Limit</th></tr>
              </thead>
              <tbody>
                {filteredAPIs.map((a) => (
                  <tr key={a.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                    <td className="px-4 py-2"><div className="font-medium">{a.name}</div><div className={`text-xs ${muted}`}>{a.base_path}</div></td>
                    <td className="px-4 py-2">{a.service}</td>
                    <td className={`px-4 py-2 font-medium ${lifecycleColor(a.lifecycle, dark)}`}>{a.lifecycle}</td>
                    <td className="px-4 py-2">{a.authentication}</td>
                    <td className="px-4 py-2">{a.rate_limit_rps} rps</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'openapi' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-left text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 font-medium">API</th><th className="px-4 py-2 font-medium">Spec URL</th><th className="px-4 py-2 font-medium">Status</th><th className="px-4 py-2 font-medium">Action</th></tr>
            </thead>
            <tbody>
              {specs.map((s) => (
                <tr key={s.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2">{s.api_name} <span className={muted}>v{s.version}</span></td>
                  <td className={`px-4 py-2 font-mono text-xs ${muted}`}>{s.spec_url}</td>
                  <td className="px-4 py-2">{s.compatibility_status}</td>
                  <td className="px-4 py-2"><button type="button" disabled={busy} onClick={() => validateSpec(s.api_name)} className="text-xs text-teal-500 hover:underline">Validate</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'sdk' && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sdks.slice(0, 24).map((s) => (
            <div key={s.id} className={`rounded-xl border p-3 ${card}`}>
              <p className="font-medium">{s.language}</p>
              <p className={`text-xs ${muted}`}>{s.api_name} v{s.version}</p>
              <a href={s.download_url} className="mt-1 block text-xs text-teal-500 hover:underline">Download SDK</a>
            </div>
          ))}
        </div>
      )}

      {tab === 'developers' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-left text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 font-medium">Developer</th><th className="px-4 py-2 font-medium">Role</th><th className="px-4 py-2 font-medium">APIs</th><th className="px-4 py-2 font-medium">Status</th></tr>
            </thead>
            <tbody>
              {developers.map((d) => (
                <tr key={d.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2"><div className="font-medium">{d.name}</div><div className={`text-xs ${muted}`}>{d.email}</div></td>
                  <td className="px-4 py-2">{d.role}</td>
                  <td className="px-4 py-2">{d.api_count}</td>
                  <td className="px-4 py-2">{d.active ? 'active' : 'inactive'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'policies' && (
        <div className="grid gap-4 md:grid-cols-2">
          {policies.map((p) => (
            <div key={p.id} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{p.name}</p>
              <p className={`text-xs uppercase ${muted}`}>{p.policy_type}</p>
              <p className={`mt-1 text-xs ${muted}`}>Applies to: {p.applies_to?.join(', ')}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'analytics' && analytics && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div><dt className={`text-xs uppercase ${muted}`}>Requests (24h)</dt><dd className="text-2xl font-semibold">{analytics.total_requests_24h.toLocaleString()}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Throughput</dt><dd className="text-2xl font-semibold">{analytics.gateway_throughput_rps.toLocaleString()} rps</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Error Rate</dt><dd className="text-2xl font-semibold">{analytics.error_rate.toFixed(2)}%</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Rate Limit Violations</dt><dd>{analytics.rate_limit_violations}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>SDK Downloads</dt><dd>{analytics.sdk_downloads}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Developer Activity</dt><dd>{analytics.developer_activity}</dd></div>
          </dl>
        </div>
      )}

      {tab === 'health' && health && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <div className="flex items-center gap-4">
            <div className="text-5xl font-bold text-teal-500">{health.api_health_score.toFixed(1)}</div>
            <div><p className="font-semibold">API Gateway Health Score</p><p className={`text-sm ${muted}`}>Validated {new Date(health.validated_at).toLocaleString()}</p></div>
          </div>
        </div>
      )}

      {tab === 'lifecycle' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-left text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 font-medium">API</th><th className="px-4 py-2 font-medium">Lifecycle</th><th className="px-4 py-2 font-medium">Deprecation</th><th className="px-4 py-2 font-medium">Action</th></tr>
            </thead>
            <tbody>
              {apis.map((a) => (
                <tr key={a.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2">{a.name}</td>
                  <td className={`px-4 py-2 ${lifecycleColor(a.lifecycle, dark)}`}>{a.lifecycle}</td>
                  <td className="px-4 py-2">{a.deprecation_status}</td>
                  <td className="px-4 py-2">
                    {a.lifecycle !== 'deprecated' && (
                      <button type="button" disabled={busy} onClick={() => deprecateAPI(a.id)} className="text-xs text-amber-500 hover:underline">Deprecate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dashboard && tab === 'overview' && <p className={`text-xs ${muted}`}>Tenant {dashboard.tenant_id} · {apis.length} APIs · {sdks.length} SDKs</p>}
    </div>
  );
}
